import { useOktaAuth } from '@okta/okta-react';
import React, { useCallback, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { HcpCountdown, HcpModal } from '.';
import { useSessionTracker } from '../hooks';
import { logout, renewSession } from '../redux';

const minutesConversion = 60 * 1000;

const defaultSessionTimeoutInMinutes = 20;
const sessionTimeoutInMinutes = Number(
  process.env.REACT_APP_SESSION_TIMEOUT_IN_MINUTES ?? defaultSessionTimeoutInMinutes
);
const sessionTimeout = sessionTimeoutInMinutes * minutesConversion;

const defaultWarningTimeoutInMinutes = 18;
const warningTimeoutInMinutes = Number(
  process.env.REACT_APP_SESSION_WARNING_IN_MINUTES ?? defaultWarningTimeoutInMinutes
);
const warningTimeout = warningTimeoutInMinutes * minutesConversion;

const timeoutDiff = sessionTimeout - warningTimeout;

if (warningTimeout === sessionTimeout) {
  // eslint-disable-next-line no-console
  console.warn(`[HCP] Session Timeout (${sessionTimeout}) and Idle Timeout (${warningTimeout}) are the same.`);
} else if (sessionTimeout < warningTimeout) {
  // eslint-disable-next-line no-console
  console.error(
    `[HCP] Session Timeout (${sessionTimeout}) is lower than the Idle Timeout (${warningTimeout}). This is a configuration error.`
  );
}

export const HcpSessionManager = () => {
  const dispatch = useDispatch();
  const [isWarned, setIsWarned] = useState(false);
  const { authState } = useOktaAuth();

  const onWarn = useCallback(() => {
    setIsWarned(true);
  }, [setIsWarned]);

  const onSessionTimeout = useCallback(() => {
    // Keep this around to clear the modal (prevents an error)
    setIsWarned(false);
    // Next tick the actual logout (prevents an error)
    setTimeout(() => dispatch(logout()), 0);
  }, [dispatch]);

  const onReset = useCallback(() => {
    if (isWarned) dispatch(renewSession());
    setIsWarned(false);
  }, [dispatch, isWarned, setIsWarned]);

  useSessionTracker({
    isActive: authState.isAuthenticated ?? false,
    onSessionTimeout,
    onWarn,
    // convert to minutes in ms
    sessionTimeout,
    // convert to minutes in ms
    warningTimeout
  });

  return (
    <HcpModal
      body={`You have been idle for ${warningTimeoutInMinutes} minutes.`}
      footer={
        <>
          <HcpCountdown className='mr-3 text-muted' timeout={timeoutDiff} onTimeout={onSessionTimeout} />
          <Button variant='danger' onClick={onSessionTimeout}>
            Sign Out
          </Button>
          <Button variant='primary' onClick={onReset}>
            Stay Signed In
          </Button>
        </>
      }
      header={<h4 className='m-0 p-0'>Are you still there?</h4>}
      modalProps={{ show: isWarned, onHide: () => null }}
    />
  );
};
